import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Variants } from '@common/types';
import { getDateWithoutTZ, getSelectedDateRangeString } from '@common/utils';
import { DropdownItem, SelectA11y } from '@base-app';
import { DateRangeDropdown, DateRangeSelection, SearchBar } from '@builder';
import { COMMON_TRANSLATION, LAYOUT_THREE } from '@constants/index';
import { useFeatureFlags } from '@modules/feature-flags/hooks';
import { FeatureFlags } from '@modules/feature-flags/types';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { useGoalsStore, useSequenceSamplesStore } from '@modules/study-insights/store';
import { SampleTrackingType, SampleCollectionLocation } from '@modules/study-insights/types';

import { StyledViewOrderIdsFilter, StyledDropdownFilters } from './ViewOrderIdsFilter.styles';
import { ViewOrderIdsFilterProps } from './ViewOrderIdsFilter.types';
import { PillId } from '../../../CollectionAndSequencing.types';

const ViewOrderIdsFilter: FC<ViewOrderIdsFilterProps> = ({ collectionLocation, onFilterChange }) => {
  const { t } = useTranslation([COMMON_TRANSLATION, STUDY_INSIGHTS_TRANSLATION]);
  const { isFeatureEnabled } = useFeatureFlags();
  const isBuccalEnabled = isFeatureEnabled(FeatureFlags.BUCCAL_SAMPLE_TYPE);
  const [queryFilter, setQueryFilter] = useState<string>('');
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeSelection | undefined>();
  const [typesFilter, setTypesFilter] = useState<SampleTrackingType[]>([]);
  const startDateGoal = useGoalsStore((store) => store.lifetimeGoal?.startDate);
  const endDateGoal = useGoalsStore((store) => store.lifetimeGoal?.endDate);
  const { selectedPill: sequenceSamplesPill, selectedDateRange } = useSequenceSamplesStore();

  const SAMPLE_TYPE_OPTIONS: DropdownItem[] = [
    {
      id: SampleTrackingType.BLOOD,
      label: t('view_order_id.blood_samples', { ns: STUDY_INSIGHTS_TRANSLATION }),
    },
    {
      id: SampleTrackingType.SALIVA,
      label: t('view_order_id.saliva_samples', { ns: STUDY_INSIGHTS_TRANSLATION }),
    },
  ]
    .concat(
      isBuccalEnabled
        ? {
            id: SampleTrackingType.BUCCAL_SWAB,
            label: t('view_order_id.buccal_samples', { ns: STUDY_INSIGHTS_TRANSLATION }),
          }
        : [],
    )
    .filter(({ id }) => !(id === SampleTrackingType.BLOOD && collectionLocation === SampleCollectionLocation.AT_HOME));

  const handleQueryFilterChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQueryFilter(e?.target?.value);
  };

  const handleDateRangeSelect = (value: DateRangeSelection): void => {
    setDateRangeFilter(value);
  };

  const handleTypesFilter = (values: DropdownItem[]): void => {
    setTypesFilter([...values.map((item: DropdownItem) => item.id as SampleTrackingType)]);
  };

  const handleQueryFilterClear = (): void => {
    setQueryFilter('');
  };

  useEffect(() => {
    onFilterChange({
      dateRange: dateRangeFilter,
      query: queryFilter,
      types: typesFilter,
    });
  }, [queryFilter, dateRangeFilter, typesFilter]);

  return (
    <StyledViewOrderIdsFilter>
      <SearchBar
        id="view-order-ids-search"
        label={''}
        name="view-order-ids-search"
        onChange={handleQueryFilterChange}
        onClear={handleQueryFilterClear}
        width="400px"
      />
      <StyledDropdownFilters>
        <DateRangeDropdown
          dateRangeErrorDescription={t('selected_date_range.error.description', {
            dateRange:
              startDateGoal &&
              endDateGoal &&
              getSelectedDateRangeString(
                new Date(getDateWithoutTZ(startDateGoal)),
                new Date(getDateWithoutTZ(endDateGoal)),
              ),
            ns: STUDY_INSIGHTS_TRANSLATION,
          })}
          dateRangeErrorTitle={t('selected_date_range.error.title', { ns: STUDY_INSIGHTS_TRANSLATION })}
          value={selectedDateRange}
          maxDate={endDateGoal ? new Date(getDateWithoutTZ(endDateGoal)) : undefined}
          minDate={startDateGoal ? new Date(getDateWithoutTZ(startDateGoal)) : undefined}
          onSelect={handleDateRangeSelect}
          layout={LAYOUT_THREE}
        />
        {sequenceSamplesPill !== PillId.AWAITING &&
          (isBuccalEnabled || collectionLocation === SampleCollectionLocation.IN_PERSON) && (
            <SelectA11y
              css={{ marginLeft: '$4' }}
              defaultValue={''}
              id="view-order-ids-type-filter"
              items={SAMPLE_TYPE_OPTIONS}
              multipleSelector={true}
              onSelectedOption={handleTypesFilter}
              placeholder={t('view_order_id.add_filter', { ns: STUDY_INSIGHTS_TRANSLATION })}
              testId="view-order-ids-type-filter"
              value={''}
              variant={Variants.SECONDARY}
            />
          )}
      </StyledDropdownFilters>
    </StyledViewOrderIdsFilter>
  );
};

export default ViewOrderIdsFilter;
